import { lazy, Suspense, ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { ArrowUpRight, Handshake, Instagram, MapPin, ShoppingBag, Menu, X, UserRound } from 'lucide-react';
import { useCart } from '@/contexts/cart-context';
import { LanguageSwitcher } from '@/components/language-switcher';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { WhatsAppIcon } from '@/components/whatsapp-icon';
import { ProductSearch } from '@/components/product-search';
import { OwnerWorkspaceRoute } from '@/components/owner-workspace-frame';
import { useLanguage } from '@/contexts/language-context';
import { isOwnerWorkspacePath } from '@/lib/owner-route';

const loadCartDrawer = () =>
  import('@/components/cart-drawer').then(({ CartDrawer }) => ({ default: CartDrawer }));
const CartDrawer = lazy(loadCartDrawer);
const CurrencyDisplay = lazy(() =>
  import('@/components/currency-switcher').then(({ CurrencyDisplay }) => ({
    default: CurrencyDisplay,
  })),
);

function DeferredCurrencyDisplay() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="min-h-11">
      {shouldLoad ? (
        <Suspense
          fallback={
            <span
              aria-hidden="true"
              className="block h-10 w-[132px] animate-pulse rounded-sm bg-muted/30"
            />
          }
        >
          <CurrencyDisplay />
        </Suspense>
      ) : (
        <span className="sr-only">{t('Currency selection')}</span>
      )}
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { cart, isOpen, setIsOpen } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const headerOffset = useRef(0);
  const lastScrollY = useRef(0);
  const cartItemCount = Number(cart?.totalQuantity || 0);
  const { t } = useLanguage();
  const showFloatingWhatsApp = !isOwnerWorkspacePath(location);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileViewportQuery = window.matchMedia('(max-width: 767px)');
    let maxOffset = 0;
    const setHeaderOffset = (offset: number) => {
      headerOffset.current = offset;
      header.style.transform = `translateY(${offset}px)`;
      const visibleHeight = `${Math.max(0, maxOffset + offset)}px`;
      if (
        document.documentElement.style.getPropertyValue('--site-header-visible-height') !==
        visibleHeight
      ) {
        document.documentElement.style.setProperty(
          '--site-header-visible-height',
          visibleHeight,
        );
      }
    };

    maxOffset = header.getBoundingClientRect().height;
    setHeaderOffset(headerOffset.current);
    const resizeObserver =
      'ResizeObserver' in window
        ? new ResizeObserver(([entry]) => {
            if (!entry) return;
            const nextMaxOffset =
              entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
            if (nextMaxOffset === maxOffset) return;
            maxOffset = nextMaxOffset;
            setHeaderOffset(headerOffset.current);
          })
        : null;
    resizeObserver?.observe(header);

    lastScrollY.current = Math.max(0, window.scrollY);

    const handleScroll = () => {
      const currentScrollY = Math.max(0, window.scrollY);
      const scrollDelta = currentScrollY - lastScrollY.current;

      if (currentScrollY <= 8) {
        setHeaderOffset(0);
      } else if (scrollDelta !== 0) {
        if (mobileViewportQuery.matches) {
          setHeaderOffset(currentScrollY > 80 && scrollDelta > 0 ? -maxOffset : 0);
        } else if (reducedMotionQuery.matches) {
          setHeaderOffset(0);
        } else {
          const nextOffset = Math.max(-maxOffset, Math.min(0, headerOffset.current - scrollDelta));
          setHeaderOffset(nextOffset);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    const handleMotionPreferenceChange = () => {
      if (reducedMotionQuery.matches) setHeaderOffset(0);
      lastScrollY.current = Math.max(0, window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    reducedMotionQuery.addEventListener('change', handleMotionPreferenceChange);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      reducedMotionQuery.removeEventListener('change', handleMotionPreferenceChange);
      document.documentElement.style.removeProperty('--site-header-visible-height');
    };
  }, []);

  if (isOwnerWorkspacePath(location)) {
    return <OwnerWorkspaceRoute>{children}</OwnerWorkspaceRoute>;
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background text-foreground">
      <header
        ref={headerRef}
        style={{ transform: 'translateY(0px)' }}
        className="sticky top-0 z-40 w-full border-b border-border bg-background/90 backdrop-blur-md will-change-transform"
      >
        <div className="container mx-auto grid grid-cols-[minmax(0,1fr)_auto] grid-rows-[5rem_auto] items-center gap-x-3 px-4 pb-3 md:px-8 lg:grid-cols-[auto_minmax(0,1fr)_12rem_auto] lg:grid-rows-1 lg:gap-x-5 lg:py-3 xl:grid-cols-[auto_minmax(0,1fr)_15rem_auto] xl:gap-x-6">
          <Link href="/" className="col-start-1 row-start-1 inline-flex items-center justify-center justify-self-start" aria-label={t('al-Malaki home')}>
            <img
              src="/al-malaki-logo-v2.webp"
              srcSet="/al-malaki-logo-v2-small.webp 96w, /al-malaki-logo-v2-128.webp 128w, /al-malaki-logo-v2.webp 160w"
              sizes="(min-width: 768px) 80px, 64px"
              alt="al-Malaki"
              width={160}
              height={114}
              fetchPriority="high"
              className="block h-11 w-auto object-contain dark:hidden md:h-14"
            />
            <img
              src="/al-malaki-logo-white-v2.webp"
              srcSet="/al-malaki-logo-white-v2-small.webp 96w, /al-malaki-logo-white-v2.webp 160w"
              sizes="80px"
              alt=""
              aria-hidden="true"
              width={160}
              height={114}
              className="hidden h-11 w-auto object-contain dark:block md:h-14"
            />
          </Link>

          <nav className="hidden items-center gap-8 text-sm font-medium tracking-wide normal-case lg:col-start-2 lg:row-start-1 lg:flex lg:pl-5 xl:gap-10 xl:pl-7">
            <Link href="/shop" className="hover:text-primary transition-colors py-2">{t('Collection')}</Link>
            <Link href="/about" className="hover:text-primary transition-colors py-2">{t('Our Craft')}</Link>
            <Link href="/owners" className="hidden py-2 transition-colors hover:text-primary xl:inline-flex">{t('Meet the owners')}</Link>
            <Link href="/locate-us" className="hover:text-primary transition-colors py-2">{t('Locate Us')}</Link>
          </nav>

          <ProductSearch />

          <div className="col-start-2 row-start-1 flex items-center justify-end gap-0 sm:gap-4 lg:col-start-4">
            <Link href="/my-atelier" className="hidden p-2 transition-colors hover:text-primary lg:inline-flex" aria-label={t('Open my atelier')}>
              <UserRound className="w-5 h-5 stroke-[1.5]" />
            </Link>
            <LanguageSwitcher />
            <div className="hidden lg:flex">
              <ThemeSwitcher />
            </div>
            <button 
              onClick={() => setIsOpen(true)}
              onPointerEnter={() => void loadCartDrawer()}
              onFocus={() => void loadCartDrawer()}
              className="p-2 relative hover:text-primary transition-colors lg:-mr-2"
               aria-label={cartItemCount > 0 ? t('Open cart, {count} {items}', { count: cartItemCount, items: cartItemCount === 1 ? t('item') : t('items') }) : t('Open cart')}
            >
              <ShoppingBag className="w-5 h-5 stroke-[1.5]" />
              {cartItemCount > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground"
                >
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 -mr-2 text-foreground lg:hidden"
              aria-label={t('Open menu')}
            >
              <Menu className="w-6 h-6 stroke-[1.5]" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col p-4 md:p-8 animate-fade-in">
          <div className="flex justify-between items-center h-12 mb-12">
             <Link href="/" onClick={() => setMobileMenuOpen(false)} className="inline-flex items-center" aria-label={t('al-Malaki home')}>
              <img
                src="/al-malaki-logo-v2.webp"
                srcSet="/al-malaki-logo-v2-small.webp 96w, /al-malaki-logo-v2.webp 160w"
                sizes="80px"
                alt="al-Malaki"
                width={160}
                height={114}
                className="block h-12 w-auto object-contain dark:hidden"
              />
              <img
                src="/al-malaki-logo-white-v2.webp"
                srcSet="/al-malaki-logo-white-v2-small.webp 96w, /al-malaki-logo-white-v2.webp 160w"
                sizes="80px"
                alt=""
                aria-hidden="true"
                width={160}
                height={114}
                className="hidden h-12 w-auto object-contain dark:block"
              />
            </Link>
             <button onClick={() => setMobileMenuOpen(false)} className="p-2 -mr-2 text-foreground" aria-label={t('Close menu')}>
              <X className="w-7 h-7 stroke-[1.5]" />
            </button>
          </div>
          <nav className="flex flex-col gap-8 text-3xl font-serif text-primary">
             <Link href="/shop" onClick={() => setMobileMenuOpen(false)} className="hover:opacity-80 transition-opacity">{t('Shop Collection')}</Link>
             <Link href="/about" onClick={() => setMobileMenuOpen(false)} className="hover:opacity-80 transition-opacity">{t('Our Craft')}</Link>
             <Link href="/owners" onClick={() => setMobileMenuOpen(false)} className="hover:opacity-80 transition-opacity">{t('Meet the owners')}</Link>
             <Link href="/locate-us" onClick={() => setMobileMenuOpen(false)} className="hover:opacity-80 transition-opacity">{t('Locate Us')}</Link>
          </nav>
          <div className="mt-8 flex flex-col gap-4 border-t border-border pt-5">
            <Link
              href="/my-atelier"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex items-center gap-3 text-lg text-foreground transition-colors hover:text-primary"
            >
              <UserRound className="h-5 w-5 stroke-[1.5]" aria-hidden="true" />
              {t('My atelier')}
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{t('Appearance')}</span>
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col">
        {children}
      </main>

      <footer className="border-t border-border mt-auto bg-muted/40">
        <div className="container mx-auto px-4 py-14 md:px-8 md:py-16">
          <div className="grid gap-12 text-center sm:grid-cols-2 sm:text-left lg:grid-cols-[minmax(220px,1.5fr)_repeat(4,minmax(0,1fr))]">
            <div className="flex flex-col items-center gap-3 sm:items-start">
              <Link href="/" className="inline-flex items-center" aria-label={t('al-Malaki home')}>
                <img
                  src="/al-malaki-logo-v2.webp"
                  srcSet="/al-malaki-logo-v2-small.webp 96w, /al-malaki-logo-v2.webp 160w"
                  sizes="80px"
                  alt="al-Malaki"
                  width={160}
                  height={114}
                  className="block h-14 w-auto object-contain dark:hidden"
                />
                <img
                  src="/al-malaki-logo-white-v2.webp"
                  srcSet="/al-malaki-logo-white-v2-small.webp 96w, /al-malaki-logo-white-v2.webp 160w"
                  sizes="80px"
                  alt=""
                  aria-hidden="true"
                  width={160}
                  height={114}
                  className="hidden h-14 w-auto object-contain dark:block"
                />
              </Link>
               <span className="text-sm font-medium tracking-wide text-muted-foreground">{t('The Rida Boutique')}</span>
              <div className="mt-1">
                 <DeferredCurrencyDisplay />
              </div>
            </div>

            <nav aria-labelledby="footer-explore-heading">
               <h2 id="footer-explore-heading" className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground">{t('Explore')}</h2>
              <div className="mt-5 flex flex-col items-center gap-3 text-xs font-medium uppercase tracking-widest text-muted-foreground sm:items-start">
                <Link href="/shop" className="transition-colors hover:text-primary">
                   {t('Shop collection')}
                </Link>
                 <Link href="/about" className="transition-colors hover:text-primary">{t('Our craft')}</Link>
                  <Link href="/owners" className="transition-colors hover:text-primary">{t('Meet the owners')}</Link>
              </div>
            </nav>

            <nav aria-labelledby="footer-care-heading">
               <h2 id="footer-care-heading" className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground">{t('Customer care')}</h2>
              <div className="mt-5 flex flex-col items-center gap-3 text-xs font-medium uppercase tracking-widest text-muted-foreground sm:items-start">
                <Link href="/my-atelier" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
                  <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                   {t('My atelier')}
                </Link>
                <Link href="/affiliate/how-it-works" className="inline-flex items-center gap-2 transition-colors hover:text-primary" data-testid="link-affiliates">
                  <Handshake className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('Affiliates')}
                </Link>
                <Link href="/locate-us" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                   {t('Locate us')}
                </Link>
                <a href="https://wa.me/917869768659" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
                  <WhatsAppIcon className="h-3.5 w-3.5" aria-hidden="true" />
                   {t('WhatsApp us')}
                  <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                </a>
              </div>
            </nav>

            <nav aria-labelledby="footer-policies-heading">
               <h2 id="footer-policies-heading" className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground">{t('Policies')}</h2>
              <div className="mt-5 flex flex-col items-center gap-3 text-xs font-medium uppercase tracking-widest text-muted-foreground sm:items-start">
                 <Link href="/policies/terms-and-conditions" className="transition-colors hover:text-primary">{t('Terms')}</Link>
                 <Link href="/policies/privacy-policy" className="transition-colors hover:text-primary">{t('Privacy')}</Link>
                 <Link href="/policies/refund-policy" className="transition-colors hover:text-primary">{t('Refunds')}</Link>
                 <Link href="/policies/cancellation-policy" className="transition-colors hover:text-primary">{t('Cancellations')}</Link>
              </div>
            </nav>

            <div>
               <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground">{t('Follow us')}</h2>
              <div className="mt-5 flex flex-col items-center gap-3 text-xs font-medium uppercase tracking-widest text-muted-foreground sm:items-start">
                <a href="https://www.instagram.com/almalaki.ridas" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
                  <Instagram className="h-3.5 w-3.5" aria-hidden="true" />
                   {t('Instagram')}
                  <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                </a>
                <a href="https://chat.whatsapp.com/LbhpOX3Lmoo6cgpdh3dhVN" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
                  <WhatsAppIcon className="h-3.5 w-3.5" aria-hidden="true" />
                   {t('WhatsApp Community')}
                  <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                </a>
                <a href="https://www.instagram.com/channel/Aba3Exo95o1c7-FQ/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
                  <Instagram className="h-3.5 w-3.5" aria-hidden="true" />
                   {t('Instagram Channel')}
                  <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                </a>
              </div>
            </div>

          </div>
        </div>
        
        <div className="border-t border-border py-4 text-center text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/60">
          <p>
             {t('Website Built by')}{" "}
            <a
              href="https://digitalkalakari.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-primary transition-colors"
            >
              Digital Kalakari
            </a>
          </p>
        </div>
      </footer>

      {showFloatingWhatsApp && (
        <a
          href="https://wa.me/917869768659"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('Chat with al-Malaki on WhatsApp')}
          data-testid="link-floating-whatsapp"
          className="group fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-foreground/15 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 md:bottom-6 md:right-6"
        >
          <WhatsAppIcon className="h-6 w-6" aria-hidden="true" />
          <span className="pointer-events-none absolute right-full mr-3 hidden whitespace-nowrap rounded-sm bg-foreground px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-background opacity-0 transition-opacity group-hover:opacity-100 md:block">
            {t('Chat on WhatsApp')}
          </span>
        </a>
      )}

      {isOpen && (
        <Suspense
          fallback={
            <div
              role="status"
              aria-label={t('Loading cart')}
              className="fixed inset-0 z-50 flex items-center justify-center bg-background/70"
            >
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-r-transparent" />
            </div>
          }
        >
          <CartDrawer />
        </Suspense>
      )}
    </div>
  );
}